import { ApiService } from "services/server";
import { NewsList } from "views";
import { CONTENT_TYPES, Formatter, TAG_TREES } from "utils";
import { normalizeNewsTagTrees, normalizeMetaTags } from "utils/normalizer";
import type { GetServerSideProps } from "next";
import type { GetContentParams } from "types";

const TABS = ["news", "events"] as const;
const MAPED_TABS = {
  fa: {
    [TABS[0]]: "اخبار",
    [TABS[1]]: "اعلانات",
  },
  en: {
    [TABS[0]]: "News",
    [TABS[1]]: "Events",
  },
} as const;

const MAPED_TAG_TREES = {
  fa: {
    [TABS[0]]: TAG_TREES.FA.NEWS,
    [TABS[1]]: TAG_TREES.FA.EVENTS,
  },
  en: {
    [TABS[0]]: TAG_TREES.EN.NEWS,
    [TABS[1]]: TAG_TREES.EN.EVENTS,
  },
} as const;

const BREADCRUMB = {
  fa: [{ title: "اخبار و اعلانات" }],
  en: [{ title: "News And Events" }],
};

const ITEMS_PER_PAGE = 6;

function NewsListPage({ data }) {
  return (
    <NewsList
      data={data}
      tabs={TABS}
      mappedTabs={MAPED_TABS}
      mappedTagTrees={MAPED_TAG_TREES}
      breadcrumb={BREADCRUMB}
      contentType={CONTENT_TYPES.NEWS}
    />
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  query,
  locale,
}) => {
  let noIndex = false;
  const apiService = new ApiService(locale);
  try {
    const page = Number.isNaN(Number(query.page)) ? 1 : Number(query.page);

    const tagTrees = [
      query.category
        ? query.category
        : TAG_TREES[locale?.toUpperCase() || "FA"]?.[
            query?.tab === "events" ? "EVENTS" : "NEWS"
          ],
    ];

    const params: GetContentParams = {
      tagTrees,
      offset: (page - 1) * ITEMS_PER_PAGE,
      size: ITEMS_PER_PAGE,
      orderBy: ["content.publish_date:desc"],
      orderTagTree: "child",
    };
    if (query.search) {
      params.fieldCode = ["title"];
      params.fieldValue = [query.search as string];
    }

    const requests = [
      apiService.getNewsList(params, { formatter: false }), // 0
      apiService.getNewsTagTrees(), // 1
      apiService.getEventsTagTrees(), // 2
      apiService.getNewsMetaTags(), // 3
      apiService.getEventMetaTags(), // 4
    ];

    let response = [];
    try {
      response = await ApiService.multipleRequest(requests);
    } catch (e) {
      console.log(`e`, e);
    }

    const newsTagTrees = normalizeNewsTagTrees(response[1]);
    const eventsTagTrees = normalizeNewsTagTrees(response[2]);
    const cmsCount = response[0]?.count || 0;
    const totalPages = Math.ceil(cmsCount / ITEMS_PER_PAGE);

    if (page > totalPages) {
      noIndex = true;
    }

    return {
      props: {
        data: {
          cmsData: Formatter.formatData({ data: response[0] }),
          cmsCount,
          totalPages,
          page,
          newsTagTrees,
          eventsTagTrees,
          newsMetaTag: normalizeMetaTags(response[3]),
          eventsMetaTag: normalizeMetaTags(response[4]),
          defaultTitle: locale === "fa" ? "اخبار و اعلانات" : "News and Events",
          noIndex,
          defaultValues: {
            search: query.search || "",
            category: query.category
              ? [newsTagTrees, eventsTagTrees][
                  query.tab === TABS[1] ? 1 : 0
                ].find((item) => item?.value === query.category) || ""
              : "",
          },
        },
      },
    };
  } catch (err) {
    console.log("err", err);
    return {
      redirect: {
        destination: "/500",
        permanent: false,
      },
    };
  }
};

export default NewsListPage;
